import React, { useState, useEffect } from 'react';
import { 
  ShoppingBag, User as UserIcon, Heart, MapPin, Menu, X, LogOut,
  LayoutDashboard, Package, Tag, UserCog, Plus, List, Settings, Users, Globe, DollarSign
} from 'lucide-react';
import { NavItem, User, WishlistItem } from '../types';

const topNavItems: NavItem[] = [
  { label: 'Philippines', path: '#' },
  { label: 'Contact', path: '#contact' },
  { label: 'Services', path: '#services' },
  { label: 'About', path: '#about' },
];

const mainNavItems: NavItem[] = [
  { label: 'Shop', path: '#shop' },
  { label: 'Jewelry', path: '#collections' },
  { label: 'Bags', path: '#bags' },
  { label: 'Fragrances', path: '#fragrances' },
];

interface NavbarProps {
  onNavigate: (page: string) => void;
  user: User | null;
  onLogout: () => void;
  activePage: string;
  wishlist?: WishlistItem[];
  onOpenWishlist?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ 
    onNavigate, 
    user, 
    onLogout, 
    activePage,
    wishlist = [],
    onOpenWishlist
}) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const isAuthenticated = !!user;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLocationClick = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    onNavigate('contact');
    setIsMobileMenuOpen(false);
    window.history.pushState(null, '', '/contact');
    setTimeout(() => {
      const mapSection = document.getElementById('location-map');
      if (mapSection) {
        mapSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, item: NavItem) => {
    e.preventDefault();
    
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    let targetPage = '';

    switch (item.label) {
      case 'Contact':
        targetPage = 'contact';
        break;
      case 'Services':
        targetPage = 'services';
        break;
      case 'About':
        targetPage = 'about';
        break;
      case 'Philippines':
        handleLocationClick();
        return;
      case 'Shop':
        targetPage = 'shop';
        break;
      case 'Jewelry':
        targetPage = 'collections';
        break;
      case 'Bags':
        targetPage = 'bags';
        break;
      case 'Fragrances':
        targetPage = 'fragrances';
        break;
      default:
        targetPage = 'home';
        break;
    }

    onNavigate(targetPage);
    window.history.pushState(null, '', targetPage === 'home' ? '/' : `/${targetPage}`);
  };

  const handleLogoClick = () => {
    onNavigate('home');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
    window.history.pushState(null, '', '/');
  };

  const handleAuthClick = () => {
    if (!isAuthenticated) {
        onNavigate('auth');
        window.history.pushState(null, '', '/auth');
    } else if (user?.role === 'admin') {
       onNavigate('admin-dashboard');
       window.history.pushState(null, '', '/admin/dashboard');
    } else {
       onNavigate('profile');
       window.history.pushState(null, '', '/profile');
    }
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getDisplayName = () => {
    if (!user) return 'Guest';
    
    if (user.role === 'admin') return 'Admin';
    
    if (user.firstName && user.firstName !== '') {
      if (user.lastName && user.lastName !== '') {
        return `${user.firstName} ${user.lastName.charAt(0)}.`;
      }
      return user.firstName;
    }
    
    if (user.name && user.name !== 'Member' && user.name !== 'New Member') {
      const nameParts = user.name.split(' ');
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts[1] : '';
      
      if (firstName && firstName.length > 0) {
        if (lastName && lastName.length > 0) {
          return `${firstName} ${lastName.charAt(0)}.`;
        }
        return firstName.length <= 12 ? firstName : firstName.substring(0, 10) + '...';
      }
      
      return user.name.length <= 12 ? user.name : user.name.substring(0, 10) + '...';
    }
    
    if (user.email) {
      return user.email.split('@')[0];
    }
    
    return 'Member';
  };

  const isItemActive = (label: string) => {
      if (label === 'Shop' && activePage === 'shop') return true;
      if (label === 'Jewelry' && activePage === 'collections') return true;
      if (label === 'Bags' && activePage === 'bags') return true;
      if (label === 'Fragrances' && activePage === 'fragrances') return true;
      if (label === 'About' && activePage === 'about') return true;
      if (label === 'Contact' && activePage === 'contact') return true;
      if (label === 'Services' && activePage === 'services') return true;
      return false;
  };

  // Render user menu - ULTRA HIGH Z-INDEX
  const renderUserMenu = () => {
    if (!user) return null;
    
    if (user.role === 'admin') {
      return (
        <div className="bg-white border border-stone-200 shadow-2xl rounded-sm py-2 w-64 text-left animate-fade-in" style={{ zIndex: 999999, position: 'relative' }}>
           <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-t border-l border-stone-200 transform rotate-45" style={{ zIndex: 999999 }}></div>
           
           <div className="relative bg-white rounded-sm" style={{ zIndex: 999999 }}>
             <div className="px-4 py-3 border-b border-stone-100 mb-2 bg-stone-50/50">
                <span className="block text-[10px] uppercase tracking-widest text-gold-600 font-bold">Admin Console</span>
                <span className="block text-sm font-serif text-stone-900 mt-1">Administrator</span>
             </div>

             <button onClick={() => {
               onNavigate('admin-dashboard');
               window.history.pushState(null, '', '/admin/dashboard');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <LayoutDashboard size={14} className="text-gold-600" /> Dashboard
             </button>

             <div className="my-2 border-t border-stone-100"></div>
             <span className="px-4 py-1 text-[9px] uppercase tracking-widest text-stone-400 font-semibold">Inventory</span>

             <button onClick={() => {
               onNavigate('admin-products');
               window.history.pushState(null, '', '/admin/products');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <List size={14} /> Products List
             </button>
             <button onClick={() => {
               onNavigate('admin-add-product');
               window.history.pushState(null, '', '/admin/add-product');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <Plus size={14} /> Add Product
             </button>
             <button onClick={() => {
               onNavigate('admin-pricing');
               window.history.pushState(null, '', '/admin/pricing');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <Tag size={14} /> Jewelry Pricing
             </button>

             <div className="my-2 border-t border-stone-100"></div>
             <span className="px-4 py-1 text-[9px] uppercase tracking-widest text-stone-400 font-semibold">Management</span>

             <button onClick={() => {
               onNavigate('admin-transactions');
               window.history.pushState(null, '', '/admin/transactions');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <DollarSign size={14} /> Transactions
             </button>
             <button onClick={() => {
               onNavigate('admin-members');
               window.history.pushState(null, '', '/admin/members');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <Users size={14} /> Members
             </button>
             <button onClick={() => {
               onNavigate('admin-settings');
               window.history.pushState(null, '', '/admin/settings');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <Globe size={14} /> Site Settings
             </button>
             
             <div className="my-2 border-t border-stone-100"></div>
             <span className="px-4 py-1 text-[9px] uppercase tracking-widest text-stone-400 font-semibold">Account</span>

             <button onClick={() => {
               onNavigate('admin-profile');
               window.history.pushState(null, '', '/admin/profile');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <UserCog size={14} /> Edit Profile
             </button>
             
             <button 
                onClick={() => {
                  onLogout();
                  window.history.pushState(null, '', '/');
                }}
                className="w-full text-left px-4 py-3 text-xs text-red-500 hover:bg-red-50 flex items-center gap-3 transition-colors mt-2 border-t border-stone-100"
             >
                <LogOut size={14} /> Sign Out
             </button>
           </div>
        </div>
      );
    }
    
    // Member Menu
    return (
        <div className="bg-white border border-stone-200 shadow-2xl rounded-sm py-2 w-56 text-left animate-fade-in" style={{ zIndex: 999999, position: 'relative' }}>
           <div className="absolute -top-2 right-4 w-4 h-4 bg-white border-t border-l border-stone-200 transform rotate-45" style={{ zIndex: 999999 }}></div>
           
           <div className="relative bg-white rounded-sm" style={{ zIndex: 999999 }}>
             <div className="px-4 py-3 border-b border-stone-100 mb-2">
                <span className="block text-[10px] uppercase tracking-widest text-gold-600 font-bold">Member</span>
                <span className="block text-sm font-serif text-stone-900 mt-1">
                  {getDisplayName()}
                </span>
                {user?.email && (
                  <span className="block text-[10px] text-stone-400 mt-1 truncate">{user.email}</span>
                )}
             </div>
             
             <button onClick={() => {
               onNavigate('profile');
               window.history.pushState(null, '', '/profile');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <UserCog size={14} /> Account Settings
             </button>
             <button onClick={() => {
               onNavigate('orders');
               window.history.pushState(null, '', '/orders');
             }} className="w-full text-left px-4 py-2 text-xs text-stone-600 hover:bg-stone-50 hover:text-stone-900 flex items-center gap-3 transition-colors">
                <Package size={14} /> My Orders
             </button>

             <button 
                onClick={() => {
                  onLogout();
                  window.history.pushState(null, '', '/');
                }}
                className="w-full text-left px-4 py-3 text-xs text-red-500 hover:bg-red-50 flex items-center gap-3 transition-colors mt-2 border-t border-stone-100"
             >
                <LogOut size={14} /> Sign Out
             </button>
           </div>
        </div>
    );
  };

  return (
    <>
      <nav
        className={`fixed top-0 w-full z-40 bg-white border-b border-stone-200 transition-all duration-300 overflow-visible ${
          isScrolled ? 'shadow-md' : ''
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 overflow-visible">
          
          {/* TOP ROW */}
          <div className="h-24 flex justify-between items-center relative overflow-visible">
            
            {/* Left: Secondary Nav (Desktop) */}
            <div className="hidden lg:flex items-center gap-8">
              {topNavItems.map((item) => (
                <a
                  key={item.label}
                  href={item.path}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`text-[10px] tracking-[0.2em] uppercase font-medium transition-colors ${
                      isItemActive(item.label) ? 'text-gold-600 font-bold' : 'text-stone-500 hover:text-stone-900'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </div>

            {/* Mobile Menu Toggle - Left side */}
            <button 
              className="lg:hidden text-stone-800 p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Center: Logo */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <h1 
                onClick={handleLogoClick}
                className="font-serif text-3xl md:text-4xl text-stone-900 tracking-wide cursor-pointer hover:opacity-80 transition-opacity"
              >
                Sparkling Gems
              </h1>
            </div>

            {/* Right: Action Icons */}
            <div className="flex items-center gap-3">
              
              {/* REMOVED: Cart Icon - Only Wishlist remains */}
              
              {/* Desktop Only: Wishlist Icon */}
              <button 
                onClick={onOpenWishlist}
                className="flex w-10 h-10 border border-stone-200 rounded items-center justify-center text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all relative"
                title="Wishlist"
              >
                <Heart size={18} strokeWidth={1.5} />
                {wishlist.length > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold w-4 h-4 flex items-center justify-center rounded-full border border-white">
                        {wishlist.length}
                    </span>
                )}
              </button>
              
              {/* Desktop Only: Map / Location Icon */}
              <button 
                onClick={() => handleLocationClick()}
                className="hidden md:flex w-10 h-10 border border-stone-200 rounded items-center justify-center text-stone-700 hover:bg-stone-50 hover:border-stone-300 transition-all"
                title="Find Jewelry Shop"
              >
                <MapPin size={18} strokeWidth={1.5} />
              </button>
              
              {/* PROFILE / LOGIN BUTTON */}
              <div className="relative group" style={{ overflow: 'visible' }}>
                  <button 
                    onClick={handleAuthClick}
                    className={`flex w-10 h-10 border rounded items-center justify-center transition-all ${isAuthenticated ? 'bg-stone-900 text-gold-400 border-stone-900' : 'border-stone-200 text-stone-700 hover:bg-stone-50'}`}
                    title={isAuthenticated ? "Account" : "Member Login"}
                  >
                    <UserIcon size={18} strokeWidth={1.5} />
                  </button>
                  
                  {/* Dropdown Menu - ULTRA HIGH Z-INDEX */}
                  {isAuthenticated && (
                      <div className="absolute right-0 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300" style={{ zIndex: 999999 }}>
                        {renderUserMenu()}
                      </div>
                  )}
              </div>
            </div>
          </div>

          {/* DIVIDER */}
          <div className="border-t border-stone-100 hidden lg:block"></div>

          {/* BOTTOM ROW: Main Nav */}
          <div className={`hidden lg:flex justify-between items-center transition-all duration-300 ${isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-14 opacity-100'}`}>
            <div className="flex items-center gap-10">
              {mainNavItems.map((item) => (
                <a
                  key={item.label}
                  href={item.path}
                  onClick={(e) => handleNavClick(e, item)}
                  className={`text-[11px] tracking-[0.2em] uppercase font-semibold transition-colors ${
                      isItemActive(item.label) ? 'text-gold-600 font-bold border-b-2 border-gold-600 pb-1' : 'text-stone-600 hover:text-stone-900'
                  }`}
                >
                  {item.label}
                </a>
              ))}
            </div>
            
            <div className="relative group" style={{ overflow: 'visible' }}>
                <button 
                    className={`text-xs font-light cursor-pointer transition-colors flex items-center gap-2 ${isAuthenticated ? 'text-stone-900 font-bold' : 'text-stone-400 hover:text-gold-600'}`} 
                    onClick={() => !isAuthenticated && handleAuthClick()}
                >
                    {getDisplayName()}
                </button>

                {/* Dropdown Menu attached to Name Text - ULTRA HIGH Z-INDEX */}
                {isAuthenticated && (
                  <div className="absolute right-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300" style={{ zIndex: 999999 }}>
                    {renderUserMenu()}
                  </div>
                )}
            </div>
          </div>

        </div>

        {/* Mobile Menu Overlay */}
        <div className={`fixed inset-0 bg-white z-30 transform transition-transform duration-500 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:hidden top-[96px] overflow-y-auto`}>
          <div className="flex flex-col p-8 space-y-6 h-full border-t border-stone-100">
              <div className="space-y-4 pb-6 border-b border-stone-100">
                 {mainNavItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.path}
                    onClick={(e) => handleNavClick(e, item)}
                    className={`block font-serif text-2xl ${isItemActive(item.label) ? 'text-gold-600' : 'text-stone-900'}`}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
              
              <div className="space-y-4">
                {topNavItems.map((item) => (
                  <a
                    key={item.label}
                    href={item.path}
                    onClick={(e) => handleNavClick(e, item)}
                    className={`block text-xs tracking-[0.2em] uppercase ${isItemActive(item.label) ? 'text-gold-600 font-bold' : 'text-stone-500'}`}
                  >
                    {item.label}
                  </a>
                ))}
              </div>

              {/* Mobile Menu Buttons - REMOVED CART BUTTON */}
              <div className="pt-8 mt-auto grid grid-cols-3 gap-4">
                 {/* REMOVED: Cart button */}
                 
                 <button onClick={() => { onOpenWishlist?.(); setIsMobileMenuOpen(false); }} className="flex flex-col items-center gap-2 text-stone-600 relative">
                    <Heart size={20} />
                    {wishlist.length > 0 && (
                        <span className="absolute top-0 right-4 bg-red-500 text-white text-[8px] w-3 h-3 flex items-center justify-center rounded-full">
                            {wishlist.length}
                        </span>
                    )}
                    <span className="text-[10px] uppercase">Wishlist</span>
                 </button>
                 
                 <button 
                    onClick={() => handleLocationClick()}
                    className="flex flex-col items-center gap-2 text-stone-600"
                  >
                    <MapPin size={20} />
                    <span className="text-[10px] uppercase">Stores</span>
                 </button>
                 
                 <button 
                    onClick={() => {
                      if (isAuthenticated) {
                        if (user?.role === 'admin') {
                          onNavigate('admin-dashboard');
                        } else {
                          onNavigate('profile');
                        }
                      } else {
                        onNavigate('auth');
                      }
                      setIsMobileMenuOpen(false);
                    }} 
                    className="flex flex-col items-center gap-2 text-stone-600"
                 >
                    {isAuthenticated ? (
                      <>
                        <UserIcon size={20} className="text-gold-600" />
                        <span className="text-[10px] uppercase">Profile</span>
                      </>
                    ) : (
                      <>
                        <UserIcon size={20} />
                        <span className="text-[10px] uppercase">Login</span>
                      </>
                    )}
                 </button>
              </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;